/**
 * Offerings API Service
 *
 * Fetches deal/offering data from the backend /api/v1/offerings endpoints.
 * Replaces hardcoded featured offer cards in InvestLayout.
 */

import { api } from './api';

export interface OfferingKPIs {
  total_slots?: number;
  filled_slots?: number;
  available_slots?: number;
  annual_allocation?: number;
  cohort?: string;
  target_return?: number;
  total_shares?: number;
  available_shares?: number;
  investor_count?: number;
  funded_percentage?: number;
  total_value?: number;
  [key: string]: unknown;
}

export interface OfferingBranding {
  imageUrl?: string;
  tenant?: string;
  location?: string;
  [key: string]: unknown;
}

export interface OfferingSettings {
  featured?: boolean;
  displayAs?: string;
  investEnabled?: boolean;
  [key: string]: unknown;
}

export interface OfferingClass {
  id: string;
  name: string;
  displayOrder: number;
  active: boolean;
  minInvest: number | null;
  maxInvest: number | null;
  terms: Record<string, unknown>;
  description: string | null;
}

export interface Offering {
  id: string;
  type: 'fund' | 'property' | 'development' | 'debt' | 'license' | 'mining';
  status: 'draft' | 'open' | 'closed' | 'exited' | 'archived';
  slug: string;
  title: string;
  summary: string | null;
  kpis: OfferingKPIs;
  sections: unknown[];
  branding: OfferingBranding;
  settings: OfferingSettings;
  raiseTarget: number | null;
  minInvest: number | null;
  raised: number | null;
  createdBy: string | null;
  increaseAccountId: string | null;
  tags?: string[];
  createdAt: string;
  updatedAt: string;
  openedAt: string | null;
  closedAt: string | null;
  classes: OfferingClass[];
}

export interface InvestCategory {
  value: string;
  label: string;
}

export const offeringsService = {
  /**
   * Fetch all published (open) offerings, optionally filtering by type.
   */
  async getOfferings(type?: string): Promise<Offering[]> {
    try {
      const params = type ? `?type=${encodeURIComponent(type)}` : '';
      const response = await api.get(`/offerings${params}`);
      const data = response as unknown as { success: boolean; offerings: Offering[] };
      return data.offerings || [];
    } catch (error) {
      console.error('[offeringsService] Failed to fetch offerings:', error);
      return [];
    }
  },

  /**
   * Fetch a single offering by ID or slug.
   */
  async getOffering(idOrSlug: string): Promise<Offering | null> {
    try {
      const response = await api.get(`/offerings/${encodeURIComponent(idOrSlug)}`);
      const data = response as unknown as { success: boolean; offering: Offering };
      return data.offering || null;
    } catch (error) {
      console.error('[offeringsService] Failed to fetch offering:', error);
      return null;
    }
  },

  /**
   * Fetch tenant-configured invest page categories.
   */
  async getCategories(): Promise<InvestCategory[]> {
    try {
      const response = await api.get('/offerings/categories/list');
      const data = response as unknown as { success: boolean; categories: InvestCategory[] };
      return data.categories || [{ value: 'all', label: 'All' }];
    } catch (error) {
      console.error('[offeringsService] Failed to fetch categories:', error);
      return [{ value: 'all', label: 'All' }];
    }
  },

  /* ------------------------------------------------------------------ */
  /*  Admin Endpoints                                                    */
  /* ------------------------------------------------------------------ */

  /**
   * List ALL offerings (any status) — admin only.
   */
  async adminGetAll(): Promise<Offering[]> {
    try {
      const response = await api.get('/offerings/admin/all');
      const data = response as unknown as { success: boolean; offerings: Offering[] };
      return data.offerings || [];
    } catch (error) {
      console.error('[offeringsService] Admin list failed:', error);
      return [];
    }
  },

  /**
   * Create a new offering — admin only.
   */
  async adminCreate(offering: {
    type: string;
    title: string;
    summary?: string;
    slug?: string;
    raiseTarget?: number;
    minInvest?: number;
    kpis?: Record<string, unknown>;
    sections?: unknown[];
    branding?: Record<string, unknown>;
    settings?: Record<string, unknown>;
    status?: string;
    increaseAccountId?: string;
  }): Promise<Offering> {
    const response = await api.post('/offerings/admin/create', offering);
    const data = response as unknown as { success: boolean; offering: Offering };
    return data.offering;
  },

  /**
   * Update an existing offering — admin only.
   */
  async adminUpdate(id: string, updates: Partial<{
    title: string;
    summary: string;
    type: string;
    status: string;
    raiseTarget: number;
    minInvest: number;
    kpis: Record<string, unknown>;
    sections: unknown[];
    branding: Record<string, unknown>;
    settings: Record<string, unknown>;
    increaseAccountId: string;
    tags: string[];
  }>): Promise<Offering> {
    const response = await api.put(`/offerings/admin/${id}`, updates);
    const data = response as unknown as { success: boolean; offering: Offering };
    return data.offering;
  },

  /**
   * Archive (soft-delete) an offering — admin only.
   */
  async adminDelete(id: string): Promise<void> {
    await api.delete(`/offerings/admin/${id}`);
  },

  /**
   * Add an investment class to an offering — admin only.
   */
  async adminAddClass(dealId: string, classData: {
    name: string;
    minInvest: number;
    maxInvest?: number;
    terms?: Record<string, unknown>;
    description?: string;
    displayOrder?: number;
  }): Promise<OfferingClass> {
    const response = await api.post(`/offerings/admin/${dealId}/classes`, classData);
    const data = response as unknown as { success: boolean; dealClass: OfferingClass };
    return data.dealClass;
  },

  /**
   * Upload a cover image for an offering — admin only.
   */
  async adminUploadCover(dealId: string, file: File): Promise<{ imageUrl: string }> {
    const fd = new FormData();
    fd.append('cover', file);
    const response = await api.post(`/offerings/admin/${dealId}/cover`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const data = response as unknown as { success: boolean; imageUrl: string };
    return { imageUrl: data.imageUrl };
  },

  /**
   * Remove the cover image for an offering — admin only.
   */
  async adminDeleteCover(dealId: string): Promise<void> {
    await api.delete(`/offerings/admin/${dealId}/cover`);
  },
};
